import { Box, Card, Typography, Button } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';

import { useNavigate, Link, useSearchParams } from 'react-router-dom';

import { useSubscription } from 'AppRoutes/context/SubscriptionContext';
import Banner from 'components/Banner';
import SEOHead from 'components/SEOHead';
import { fetchBeerPacks } from 'services/firebase/FetchBeerPack';
import { getActivePromotionalBanner } from 'services/firebase/promotionalBannerService';
import { getSubscriptionHero } from 'services/firebase/subscriptionHeroService';
import { BeerPack } from 'utils/types/BeerPack';
import type { PromotionalBanner } from 'utils/types/PromotionalBanner';
import type { SubscriptionCampaignConfig } from 'utils/types/SubscriptionCampaignConfig';
import type { SubscriptionHero } from 'utils/types/SubscriptionHero';
import MonthBeer from 'components/MonthBeer';
import SubscriptionPlansSection from 'components/SubscriptionPlansSection';
import { analyticsService } from 'services/analytics/analyticsService';
import { seoConfig } from 'utils/seoConfig';
import { getHighlightedPackId } from 'utils/subscriptionPlanFeatures';

const BENEFITS = [
  'Entrega rápida',
  'Cancelamento a qualquer momento',
  'Descontos exclusivos na loja',
];

const getSeoPack = (packs: BeerPack[]): BeerPack | null => {
  if (packs.length === 0) return null;
  const highlightedId = getHighlightedPackId(packs);
  return packs.find(pack => pack.id === highlightedId) ?? packs[0];
};

interface BeerSubscriptionProps {
  campaign?: SubscriptionCampaignConfig;
}

const BeerSubscription = ({ campaign }: BeerSubscriptionProps = {}) => {
  const [packs, setPacks] = useState<BeerPack[]>([]);
  const [hero, setHero] = useState<SubscriptionHero | null>(null);
  const [promotionalBanner, setPromotionalBanner] =
    useState<PromotionalBanner | null>(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setSelectedPlan } = useSubscription();

  useEffect(() => {
    if (campaign) return;
    if (searchParams.get('utm_campaign') === 'beertrip10anos') {
      navigate(`/beertrip10anos?${searchParams.toString()}`, { replace: true });
    }
  }, [campaign, navigate, searchParams]);

  useEffect(() => {
    const loadPacks = async () => {
      const fetchedPacks = await fetchBeerPacks();
      setPacks(fetchedPacks);
      analyticsService.trackSubscriptionFlow('personal_form');
    };
    loadPacks();
  }, [campaign]);

  useEffect(() => {
    let cancelled = false;
    getSubscriptionHero()
      .then(data => {
        if (!cancelled && data) setHero(data);
      })
      .catch(() => {
        if (!cancelled) setHero(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (campaign?.bannerImage) return;

    let cancelled = false;
    getActivePromotionalBanner('home')
      .then(data => {
        if (!cancelled) setPromotionalBanner(data);
      })
      .catch(() => {
        if (!cancelled) setPromotionalBanner(null);
      });

    return () => {
      cancelled = true;
    };
  }, [campaign?.bannerImage]);

  const handleSubscription = useCallback(
    (pack: BeerPack, withCup = false) => {
      const selectedPlan = withCup
        ? {
            type: 'COM COPO',
            price: pack.priceWithCup.discounted,
            originalPrice: pack.priceWithCup.original,
            label: pack.label,
            quantity: 1,
            total: parseFloat(pack.priceWithCup.discounted),
            copo: true,
            pack: pack.label,
            subscriptionID: '',
          }
        : {
            type: '',
            price: pack.priceWithoutCup.discounted,
            originalPrice: pack.priceWithoutCup.original,
            label: pack.label,
            quantity: 1,
            total: parseFloat(pack.priceWithoutCup.discounted),
            copo: false,
            pack: pack.label,
            subscriptionID: '',
          };

      setSelectedPlan(selectedPlan);

      analyticsService.trackCta('subscription_select_plan', pack.label, {
        subscription_plan: pack.label,
        price: parseFloat(selectedPlan.price),
      });
      analyticsService.trackSubscriptionFlow(
        'shipping_options',
        pack.label,
        parseFloat(selectedPlan.price),
      );

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      navigate('/subscription');
    },
    [navigate, setSelectedPlan],
  );

  const seoPack = getSeoPack(packs);

  const organizationData = {
    '@type': 'Organization',
    name: 'Clube da 42',
    description: 'Clube de assinatura de cervejas artesanais',
    url: seoConfig.site.url,
    logo: seoConfig.site.logo,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Avenida 42',
      addressCountry: 'BR',
    },
    sameAs: [
      'https://www.instagram.com/clubeda42',
      'https://www.facebook.com/clubeda42',
    ],
  };

  const productData = seoPack
    ? {
        '@type': 'Product',
        name: seoPack.label,
        description: seoPack.description,
        image: seoPack.image,
        brand: { '@type': 'Brand', name: 'Clube da 42' },
        offers: [
          {
            '@type': 'Offer',
            price:
              seoPack.priceWithCup?.discounted ||
              seoPack.priceWithoutCup?.discounted,
            priceCurrency: 'BRL',
            availability: 'https://schema.org/InStock',
            seller: { '@type': 'Organization', name: 'Clube da 42' },
          },
        ],
      }
    : null;

  const scrollToPlanos = () =>
    document.getElementById('planos')?.scrollIntoView({ behavior: 'smooth' });

  const subscribeCtaSx = {
    bgcolor: '#c96c1e',
    color: '#fff',
    borderRadius: '12px',
    py: 1.5,
    fontWeight: 700,
    fontSize: { xs: '1rem', sm: '1.1rem' },
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': { bgcolor: '#a85818', boxShadow: 'none' },
  } as const;

  const renderHeroImage = (variant: 'mobile' | 'desktop') => {
    const isMobile = variant === 'mobile';

    return (
      <Box
        sx={{
          borderRadius: '16px',
          overflow: 'hidden',
          bgcolor: '#ffffff',
          width: '100%',
          height: isMobile ? 'auto' : '100%',
          minHeight: isMobile ? undefined : { md: '68vh' },
          display: 'flex',
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: 'center',
        }}
      >
        {hero?.image ? (
          <Box
            component="img"
            src={hero.image}
            alt={hero.alt ?? 'Clube da 42 - Cervejas artesanais por assinatura'}
            loading="eager"
            sx={{
              width: '100%',
              height: isMobile ? 'auto' : '100%',
              maxHeight: isMobile ? { xs: 280, sm: 340 } : undefined,
              display: 'block',
              objectFit: isMobile ? 'contain' : 'cover',
              objectPosition: isMobile ? 'top center' : 'center center',
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              minHeight: isMobile ? { xs: 240, sm: 280 } : undefined,
              bgcolor: '#ffffff',
            }}
          />
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ bgcolor: '#ffffff' }}>
      {(campaign?.bannerImage ?? promotionalBanner?.image) && (
        <Banner
          imageSrc={campaign?.bannerImage ?? promotionalBanner?.image}
          storageKey={
            campaign?.bannerStorageKey ?? promotionalBanner?.storageKey
          }
          alt={promotionalBanner?.alt}
          ctaText={promotionalBanner?.ctaText}
          ctaUrl={promotionalBanner?.ctaUrl}
        />
      )}
      <SEOHead
        title="Clube da 42 - Cervejas Artesanais por Assinatura | Cervejaria 42"
        description="Descubra cervejas artesanais únicas todo mês com o Clube da 42. Assinatura mensal de cervejas selecionadas da Avenida 42 e outras cervejarias independentes. Planos com e sem copo."
        keywords="cerveja artesanal, clube de cerveja, assinatura cerveja, cervejaria 42, cervejas especiais, clube da 42, cerveja por assinatura, cerveja artesanal por assinatura"
        structuredData={organizationData}
      />
      {productData && <SEOHead structuredData={productData} />}

      {/* ── HERO SECTION ──────────────────────────────────────────── */}
      <Box component="section">
        {/* Layout: coluna única no mobile, duas colunas no desktop — full width */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'stretch',
            minHeight: { md: '68vh' },
            pt: { xs: 3, sm: 5 },
          }}
        >
          {/* Coluna esquerda: imagem (só aparece no desktop) */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              width: '50%',
              flexShrink: 0,
            }}
          >
            {renderHeroImage('desktop')}
          </Box>

          {/* Coluna direita: todo o conteúdo */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              px: { xs: 2, sm: 3, md: 5 },
              pb: { xs: 5, md: 6 },
              justifyContent: { md: 'center' },
            }}
          >
            {/* Headline — 3 linhas, "Todo mês." em laranja */}
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '2.75rem' },
                fontWeight: 500,
                color: '#1a1008',
                lineHeight: 1.2,
                mb: 1.5,
              }}
            >
              A assinatura de cervejas
              <br />
              <Box
                component="span"
                sx={{ color: '#c96c1e', fontStyle: 'italic' }}
              >
                que nunca repete um rótulo.
              </Box>
            </Typography>

            {/* Subtítulo */}
            <Typography
              sx={{
                fontSize: { xs: '1rem', sm: '1.1rem' },
                fontWeight: 600,
                color: '#6b5c4a',
                mb: { xs: 2, md: 4 },
                lineHeight: 1.5,
              }}
            >
              Todos os meses criamos duas cervejas inéditas. Cada lote é
              produzido uma única vez.
            </Typography>

            {/* CTA — mobile: acima da imagem para não exigir scroll */}
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={scrollToPlanos}
              sx={{
                ...subscribeCtaSx,
                display: { xs: 'flex', md: 'none' },
                mb: { xs: 2, md: 3 },
              }}
            >
              Conheça nossos planos
            </Button>

            {/* Imagem — visível só no mobile, entre subtítulo e rating */}
            <Box
              sx={{
                display: { xs: 'block', md: 'none' },
                mt: { xs: 1, md: 0 },
                mb: { xs: 2, md: 3 },
                mx: { xs: -2, sm: -3 },
                width: { xs: 'calc(100% + 32px)', sm: 'calc(100% + 48px)' },
              }}
            >
              {renderHeroImage('mobile')}
            </Box>

            {/* Rating */}
            <Link
              to="/depoimentos"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 3,
                  cursor: 'pointer',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '1.75rem',
                    fontWeight: 500,
                    color: '#1a1008',
                    lineHeight: 1,
                  }}
                >
                  4,9
                </Typography>
                <Box sx={{ display: 'flex', gap: '2px' }}>
                  {[0, 1, 2, 3, 4].map(i => (
                    <Box
                      key={i}
                      component="span"
                      sx={{ color: '#FFD700', fontSize: '18px', lineHeight: 1 }}
                    >
                      ★
                    </Box>
                  ))}
                </Box>
                <Typography
                  sx={{
                    fontSize: '0.8rem',
                    color: '#c96c1e',
                    textDecoration: 'underline',
                    fontWeight: 400,
                  }}
                >
                  87 avaliações
                </Typography>
              </Box>
            </Link>

            {/* Benefícios */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                mb: 4,
              }}
            >
              {BENEFITS.map(benefit => (
                <Box
                  key={benefit}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      bgcolor: '#c96c1e',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        color: '#fff',
                        fontSize: '12px',
                        fontWeight: 500,
                        lineHeight: 1,
                      }}
                    >
                      ✓
                    </Box>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      fontWeight: 400,
                      color: '#1a1008',
                    }}
                  >
                    {benefit}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* CTA principal — desktop: após benefícios */}
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={scrollToPlanos}
              sx={{
                ...subscribeCtaSx,
                display: { xs: 'none', md: 'flex' },
              }}
            >
              Conheça nossos planos
            </Button>
          </Box>
        </Box>

        {/* Prova social + depoimentos — abaixo do hero, centralizado */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            mt: { xs: 3, md: 4 },
            mb: { xs: 2, md: 3 },
            px: { xs: 2, sm: 3 },
            gap: 2,
          }}
        >
          <Box
            sx={{
              bgcolor: '#f7f2ec',
              border: '1px solid #e8ddd1',
              borderRadius: '999px',
              px: 2,
              py: 0.75,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: '#c96c1e',
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#1a1008' }}
            >
              +600 assinantes ativos... e contando!
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/depoimentos')}
            sx={{
              borderColor: '#c96c1e',
              color: '#c96c1e',
              borderRadius: '12px',
              py: 1.5,
              px: 4,
              fontWeight: 500,
              fontSize: '1rem',
              textTransform: 'none',
              minWidth: { xs: '100%', sm: 280 },
              maxWidth: 400,
              '&:hover': {
                borderColor: '#a85818',
                color: '#a85818',
                bgcolor: 'transparent',
              },
            }}
          >
            Ver depoimentos
          </Button>
        </Box>
      </Box>

      {/* ── PLANOS ────────────────────────────────────────────────── */}
      <Box
        component="section"
        id="planos"
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 5, sm: 6 },
          bgcolor: '#f7f2ec',
          overflow: 'visible',
        }}
      >
        <SubscriptionPlansSection
          packs={packs}
          onSelectPlan={pack => handleSubscription(pack)}
        />
      </Box>

      {/* ── CAIXA DO MÊS ─────────────────────────────────────────── */}
      <MonthBeer />

      {/* ── SOBRE O CLUBE ─────────────────────────────────────────── */}
      <section aria-labelledby="about-clube">
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 4, sm: 6 },
            maxWidth: 960,
            mx: 'auto',
          }}
        >
          <Card
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              borderRadius: '16px',
              overflow: 'hidden',
              border: '1px solid #e8ddd1',
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: { xs: 200, sm: 'auto' },
                overflow: 'hidden',
              }}
            >
              <Box
                component="img"
                src="https://firebasestorage.googleapis.com/v0/b/clube-42-3baa5.firebasestorage.app/o/homem_segurando_caixa_30.png?alt=media&token=4c171790-04aa-4762-a83e-c6a8e2718612"
                alt="Clube da 42"
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </Box>
            <Box
              sx={{
                flex: 2,
                bgcolor: '#c96c1e',
                color: '#fff',
                p: { xs: 3, sm: 4 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography
                id="about-clube"
                component="h2"
                sx={{
                  fontSize: { xs: '1.5rem', sm: '2rem' },
                  fontWeight: 500,
                  mb: 2,
                }}
              >
                Clube da 42?
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  fontWeight: 400,
                  mb: 2,
                  fontStyle: 'italic',
                  opacity: 0.9,
                }}
              >
                Mais do que uma assinatura, uma experiência cervejeira
                exclusiva.
              </Typography>
              <Typography
                sx={{
                  fontWeight: 400,
                  mb: 2,
                  lineHeight: 1.6,
                  fontSize: '0.95rem',
                }}
              >
                Nosso propósito evoluiu: entregar a você cervejas artesanais
                autorais, produzidas em lotes únicos e limitados, criadas
                especialmente para membros do clube. Todo mês, você recebe
                rótulos inéditos, irrepetíveis e fora do mercado tradicional —
                cervejas desenvolvidas com identidade, criatividade e
                personalidade, feitas para quem valoriza exclusividade de
                verdade.
              </Typography>
              <Typography
                sx={{
                  fontWeight: 400,
                  textAlign: 'center',
                  fontSize: '0.95rem',
                  opacity: 0.9,
                }}
              >
                No Clube da 42, cada caixa é uma edição única.
                <br />
                <strong>Bebeu, viveu. Acabou, virou história.</strong>
              </Typography>
            </Box>
          </Card>
        </Box>
      </section>
    </Box>
  );
};

export default BeerSubscription;
